import React from 'react';
import { FileText, Copyright, AlertTriangle, Shield, Mail } from 'lucide-react';

const LegalPage = () => {
  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#0D0D0D] to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Policies</span>
            <h1 className="heading-hero mt-4 mb-6">
              Legal <span className="text-[#C9A962]">Notice</span>
            </h1>
            <p className="body-large">
              Welcome to the official website of Joy Ogbekene, Voiceover Artist. This page 
              explains how the materials on this website may be viewed and used. The purpose 
              is to respect creative ownership while professionally showcasing voiceover work.
            </p>
          </div>
        </div>
      </section>

      {/* Legal Sections */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="space-y-12">
            
            {/* Terms of Use */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/20 transition-colors">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center">
                  <FileText size={24} className="text-[#C9A962]" />
                </div>
                <div className="flex-1">
                  <h2 className="heading-2 mb-6">Terms of Use</h2>
                  <div className="space-y-4 text-[#B8B8B8]">
                    <p className="body-medium leading-relaxed">
                      This website presents voiceover work performed by Joy Ogbekene. Audio samples, 
                      demos, and project clips are provided to demonstrate vocal style, performance 
                      ability, and professional experience.
                    </p>
                    <p className="body-medium leading-relaxed">
                      Some samples on this site include work created for clients, brands, or productions. 
                      These are displayed strictly for portfolio purposes.
                    </p>
                    <p className="body-medium leading-relaxed">
                      Visitors may listen to or download samples for review and evaluation only. However, 
                      these recordings may not be used in any project or media without proper authorization 
                      from the appropriate rights holder.
                    </p>
                    <div className="mt-6 p-4 bg-[#0D0D0D] rounded-lg border border-[#2A2A2A]">
                      <p className="text-white font-medium mb-3">This includes, but is not limited to:</p>
                      <ul className="space-y-2 text-[#B8B8B8] text-sm">
                        <li className="flex items-start gap-2">
                          <span className="text-[#C9A962] mt-1">•</span>
                          Commercial or broadcast use
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#C9A962] mt-1">•</span>
                          Online or internal company use
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#C9A962] mt-1">•</span>
                          Editing, remixing, or repurposing recordings
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-[#C9A962] mt-1">•</span>
                          AI systems, voice cloning, machine learning, or synthetic voice training
                        </li>
                      </ul>
                    </div>
                    <p className="body-medium leading-relaxed mt-4">
                      Demo materials are for demonstration of work history and performance only, not for reuse.
                    </p>
                    <p className="body-medium leading-relaxed text-[#C9A962]">
                      For licensed use or new bookings, please make a formal inquiry.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Copyright Notice */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/20 transition-colors">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center">
                  <Copyright size={24} className="text-[#C9A962]" />
                </div>
                <div className="flex-1">
                  <h2 className="heading-2 mb-6">Copyright Notice</h2>
                  <div className="space-y-4 text-[#B8B8B8]">
                    <p className="body-medium leading-relaxed">
                      The voice performances of Joy Ogbekene are her professional work and may not be 
                      extracted, copied, or reused.
                    </p>
                    <p className="body-medium leading-relaxed">
                      However, some audio samples may contain material owned by third-party clients, 
                      agencies, producers, or brands. All trademarks, brand elements, scripts, music, 
                      and production content remain the property of their respective owners.
                    </p>
                    <p className="body-medium leading-relaxed">
                      Nothing on this website transfers ownership rights to visitors.
                    </p>
                    <p className="body-medium leading-relaxed text-[#E5D4A1]">
                      Unauthorized use of any content may violate copyright, performance rights, or 
                      brand ownership laws.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/20 transition-colors">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center">
                  <AlertTriangle size={24} className="text-[#C9A962]" />
                </div>
                <div className="flex-1">
                  <h2 className="heading-2 mb-6">Limitation of Liability</h2>
                  <div className="space-y-4 text-[#B8B8B8]">
                    <p className="body-medium leading-relaxed">
                      This website serves as a professional portfolio and informational resource. 
                      Joy Ogbekene is not responsible for:
                    </p>
                    <ul className="space-y-2 text-[#B8B8B8]">
                      <li className="flex items-start gap-2">
                        <span className="text-[#C9A962] mt-1">•</span>
                        Unauthorized third-party use of showcased materials
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#C9A962] mt-1">•</span>
                        Misuse of downloaded samples
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#C9A962] mt-1">•</span>
                        Technical interruptions or temporary unavailability
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#C9A962] mt-1">•</span>
                        Any damages resulting from improper use of website content
                      </li>
                    </ul>
                    <p className="body-medium leading-relaxed mt-4">
                      Use of materials from this site is at the user's discretion and responsibility.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Privacy Statement */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/20 transition-colors">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center">
                  <Shield size={24} className="text-[#C9A962]" />
                </div>
                <div className="flex-1">
                  <h2 className="heading-2 mb-6">Privacy Statement</h2>
                  <div className="space-y-4 text-[#B8B8B8]">
                    <p className="body-medium leading-relaxed">
                      The contact details provided on this website, including{' '}
                      <a 
                        href="mailto:legal@joyogbekene.com" 
                        className="text-[#C9A962] hover:text-[#E5D4A1] transition-colors"
                      >
                        legal@joyogbekene.com
                      </a>, 
                      are made available solely for legitimate business inquiries related to voiceover services.
                    </p>
                    <p className="body-medium leading-relaxed">
                      This contact information may not be used for spam, data harvesting, or unsolicited marketing.
                    </p>
                    <p className="body-medium leading-relaxed">
                      Professional communications are handled with discretion and will not be shared with 
                      third parties without consent, except where required by law.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Showreel Notice */}
      <section className="py-12 bg-[#0D0D0D] border-t border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-[#161616]/50 border border-[#1A1A1A] rounded-xl p-6 text-center">
            <p className="text-[#707070] text-sm italic">
              Showreel material used with permission or under fair portfolio use.
            </p>
          </div>
        </div>
      </section>

      {/* Contact for Legal */}
      <section className="py-16 bg-[#0A0A0A] border-t border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-3 bg-[#161616] border border-[#2A2A2A] rounded-full px-6 py-3">
            <Mail size={18} className="text-[#C9A962]" />
            <p className="text-[#B8B8B8]">
              Legal inquiries:{' '}
              <a
                href="mailto:legal@joyogbekene.com"
                className="text-[#C9A962] hover:text-[#E5D4A1] transition-colors"
              >
                legal@joyogbekene.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
